// src/modules/notifications/registry.jsx
//
// Presentation for a notification type: icon, colour, label.
//
// This mirrors the backend registry (services/notification/registry.js) but
// deliberately does NOT duplicate its routing. The backend resolves the deep
// link once, at write time, and stores it on the row — so the bell, the browser
// tray and this page all navigate to the same string. A second route table here
// would drift from that one, which is exactly the bug this rebuild removes.
//
// Unknown types render with a neutral icon rather than disappearing. The old
// bell filtered on a hardcoded prefix whitelist, so any new module's
// notifications were silently swallowed and nobody found out for weeks.

import React from 'react';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  BellOutlined,
  CarOutlined,
  InboxOutlined,
  SafetyOutlined,
  ToolOutlined,
} from '@ant-design/icons';

// Severity drives colour. These are semantic, not brand, colours — they say
// "this needs attention", not "this is our accent".
export const SEVERITY_COLOR = {
  info: '#1677ff',
  success: '#52c41a',
  warning: '#fa8c16',
  critical: '#ff4d4f',
};

const ICONS = {
  clock: ClockCircleOutlined,
  'check-circle': CheckCircleOutlined,
  'alert-triangle': WarningOutlined,
  'alert-octagon': CloseCircleOutlined,
  truck: CarOutlined,
  package: InboxOutlined,
  shield: SafetyOutlined,
  tool: ToolOutlined,
  bell: BellOutlined,
};

// icon key per type, matching what the backend registry declares.
const TYPE_ICON = {
  approval_pending: 'clock',
  approval_update: 'check-circle',
  approval_callback_failed: 'alert-octagon',
  inventory_transfer_pending_approval: 'clock',
  inventory_transfer_approved: 'check-circle',
  inventory_transfer_dispatched: 'truck',
  inventory_transfer_received_at_ho: 'package',
  inventory_transfer_verified: 'check-circle',
  inventory_transfer_discrepancy: 'alert-triangle',
};

export const CATEGORY_LABEL = {
  APPROVALS: 'Approvals',
  INVENTORY: 'Inventory & Transfers',
  ACCOUNTING: 'Accounting',
  HR: 'HR & Payroll',
  CALLS_LEADS: 'Calls & Leads',
  SECURITY: 'Security',
  SYSTEM: 'System & Ops alerts',
};

export const CHANNEL_LABEL = {
  inApp: 'In-app',
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

export function severityColor(severity) {
  return SEVERITY_COLOR[severity] || SEVERITY_COLOR.info;
}

export function NotificationIcon({ type, severity, style = {} }) {
  const Icon = ICONS[TYPE_ICON[type] || 'bell'] || BellOutlined;
  return <Icon style={{ color: severityColor(severity), ...style }} />;
}

/**
 * "12m ago". Cheap and dependency-free; the absolute timestamp is always one
 * hover away in a tooltip, so this only has to be readable, not precise.
 */
export function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return 'just now';
  if (secs < 90) return 'a minute ago';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

/** Today / Yesterday / Earlier — the only grouping a notification list needs. */
export function dayBucket(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (d >= startOfToday) return 'Today';
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (d >= startOfYesterday) return 'Yesterday';
  return 'Earlier';
}
